import ModelIcon from '@/components/model-icon';
import Page, { PageProps } from '@/components/page';
import PageHeader from '@/components/page/header';
import useQueryHelper from '@/hooks/use-query-helper';
import { getPrivateModelConnectors } from '@/lib/services/connector.service';
import { getAllPrivateModels } from '@/lib/services/private-model.service';
import { Box } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { QueryKey } from '@/lib/query-client';
import StatusChip, { ChipStatus } from '@/components/chip/status-chip';
import InlineToast from '@/components/toasts/inline';
import CardGrid from '@/components/card-grid';
import { GridCard } from '@/components/card-grid/helpers';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import Tooltip from '@/components/tooltip';
import Button from '@/components/button';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';
import useDrawer from '@/hooks/use-drawer.hook';
import { createUseStyles } from 'react-jss';
import FlatButton from '@/components/flat-button';
import { useNavigate } from '@tanstack/react-router';
import { ScrollAnchorId } from '@/components/scroll-anchor/helpers';

const useStyles = createUseStyles({
  privateModels: {
    '& .grid-card': {
      '& .title': {
        maxHeight: 48,
        overflow: 'hidden',
      },
    },
  },
});

const PrivateModelsPage: FC<PageProps> = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { canChangePrivateModels, getTooltip, hasPolicyV1 } = useFeature();

  const goToOrgPolicy = (section: 'reroute' | 'portal') => {
    const isReroute = section === 'reroute';
    const to = hasPolicyV1 ? '/config/default-policy' : '/config/policy/default';
    const servicesTab = hasPolicyV1 ? 'services' : 'service-access';
    const tab = isReroute ? servicesTab : 'portal';
    const hash: ScrollAnchorId | undefined = isReroute ? 'reroute-controls' : undefined;
    navigate({ to, search: { tab }, hash });
  };
  const connectorQuery = useQuery([QueryKey.ConnectorModelsList], async () =>
    getPrivateModelConnectors()
  );
  const { data: allModelConnectors } = connectorQuery;
  const { showLoader: connectorsLoading } = useQueryHelper(connectorQuery);
  const hasConnector = !!allModelConnectors?.length;

  const query = useQuery([QueryKey.PrivateModelsList], async () => getAllPrivateModels());
  const { data: privateModelData, refetch } = query;
  const { showLoader: privateModelsLoading } = useQueryHelper(query);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const { openDrawer } = useDrawer();

  const handleView = useCallback(
    (modelId?: string) => {
      openDrawer('private-model', { id: modelId || null });
    },
    [openDrawer]
  );

  const enabledModelCards: GridCard[] = useMemo(() => {
    return (privateModelData || []).map(({ id, name, modelId, status }) => {
      return {
        id: `${id}-${status}`,
        IconEl: <ModelIcon modelId={modelId} />,
        children: <StatusChip value={status as ChipStatus} size="x-small" />,
        title: name,
        detail: modelId,
        onClick: () => handleView(id),
      };
    });
  }, [privateModelData, handleView]);

  if (connectorsLoading) {
    return null;
  }

  return (
    <Page title="Private Models" query={query}>
      {hasConnector && (
        <>
          <PageHeader>
            <PageBlurb>
              Connect your privately hosted models directly to our platform by simply adding their
              connection details. Once configured, seamlessly use these models with the
              <FlatButton
                label="Claude Code Model Router"
                color="link"
                inline
                dotdot={false}
                onClick={() => goToOrgPolicy('reroute')}
              />{' '}
              or
              <FlatButton
                label="Portal"
                color="link"
                inline
                dotdot={false}
                onClick={() => goToOrgPolicy('portal')}
              />
              .
            </PageBlurb>

            <PageTools>
              <Tooltip title={getTooltip('add-private-model')} disabled={canChangePrivateModels}>
                <Button
                  label="Add Model"
                  icon="plus"
                  size="small"
                  onClick={() => handleView()}
                  disabled={!canChangePrivateModels}
                />
              </Tooltip>
            </PageTools>
          </PageHeader>
          <PageFeatureToast featureId="change-private-model" can={canChangePrivateModels} />
          <Box className={styles.privateModels}>
            <CardGrid
              title="Enabled Models"
              cards={enabledModelCards}
              showAdd={false}
              onRefresh={handleRefresh}
              loading={privateModelsLoading}
            />
          </Box>
        </>
      )}

      {!hasConnector && (
        <Box>
          <InlineToast level="warning" message="Create a connector to enable private models" />
        </Box>
      )}
    </Page>
  );
};

export default PrivateModelsPage;
